import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useSidebar } from '../../context/sidebar-context';
import { useTheme } from '../../context/theme-context';
import {
  LayoutDashboard,
  Calendar,
  BookOpen,
  Briefcase,
  CreditCard,
  LogOut,
  UserCog,
  Clock,
  Star,
  ChevronDown,
  ChevronLeft,
  Building2,
  DollarSign,
  AlertTriangle,
  Lock,
  Camera
} from 'lucide-react';
import {useAuthViewModel} from "../../view-model/auth-model-view";
import {observer} from "mobx-react-lite";
import professionalStore from "../../store/professional-store";
import LogoutConfirmationModal from "../ui/logout-confirmation-modal";
import UpgradeRequiredModal from "../ui/upgrade-required-modal";
import { useTranslation } from 'react-i18next';
import subscriptionStore from '../../store/subscription-store';
import companyStore from "../../store/company-store";
import {Company} from "../../model/professional/compagny-model";


const Sidebar: React.FC = observer(() => {
  const { isSidebarOpen, isCollapsed, toggleCollapsed, closeSidebar } = useSidebar();
  const { theme, toggleTheme } = useTheme();
  const authViewModel = useAuthViewModel();
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [lockedFeature, setLockedFeature] = useState<{ name: string; plan: 'Premium' | 'Premium+' }>({ name: '', plan: 'Premium' });
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'fr';

  const [isCompanyDropdownOpen, setIsCompanyDropdownOpen] = useState(false);
  const [activeCompany, setActiveCompany] = useState<Company | undefined>(undefined);

  useEffect(() => {
    if (companyStore.getCompanies && companyStore.getCompanies.length > 0) {
      const savedCompanyId = localStorage.getItem('selectedCompanyId');
      let selectedCompany = companyStore.getCompanies[0]; // Par défaut la première

      if (savedCompanyId) {
        const foundCompany = companyStore.getCompanies.find(c => c.id === savedCompanyId);
        if (foundCompany) {
          selectedCompany = foundCompany;
        }
      }

      setActiveCompany(selectedCompany);
      companyStore.setIdCompanySelectedDefault(selectedCompany.id);
    }
  }, []);

  const handleCompanyChange = (company: Company) => {
    setActiveCompany(company);
    setIsCompanyDropdownOpen(false);
    companyStore.setIdCompanySelectedDefault(company.id);
    localStorage.setItem('selectedCompanyId', company.id);
    window.location.reload();
  };

  // Vérifier les features disponibles
  const hasAdvancedAnalytics = subscriptionStore.currentSubscription?.advancedAnalytics ?? false;
  const hasReviewSystem = subscriptionStore.currentSubscription?.reviewSystem ?? false;
  const hasOnlinePayment = subscriptionStore.currentSubscription?.onlinePayment ?? false;

  const navGroups = [
    {
      category: t('sidebar.principal'),
      items: [
        { path: `/${currentLang}/provider/dashboard`, label: t('sidebar.dashboard'), icon: <LayoutDashboard size={16} /> },
        { path: `/${currentLang}/provider/calendar`, label: t('sidebar.calendar'), icon: <Calendar size={16} /> },
        // Afficher Réservations avec badge Premium si désactivé
        { path: `/${currentLang}/provider/reservations`, label: t('sidebar.reservations'), icon: <BookOpen size={16} />, locked: !hasAdvancedAnalytics },
      ]
    },
    {
      category: t('sidebar.gestion'),
      items: [
        { path: `/${currentLang}/provider/services`, label: t('sidebar.services'), icon: <Briefcase size={16} /> },
        { path: `/${currentLang}/provider/availability`, label: t('sidebar.availability'), icon: <Clock size={16} /> },
        // Afficher Avis avec badge Premium si désactivé
        { path: `/${currentLang}/provider/reviews`, label: t('sidebar.reviews'), icon: <Star size={16} />, locked: !hasReviewSystem },
        { path: `/${currentLang}/provider/companies`, label: t('sidebar.companies', 'Entreprises'), icon: <Building2 size={16} /> },
        { path: `/${currentLang}/provider/billing`, label: t('sidebar.billing', 'Facturation'), icon: <CreditCard size={16} /> },
        {
          path: `/${currentLang}/provider/stripe-connect`,
          label: t('sidebar.stripeConnect', 'Solde'),
          icon: professionalStore.needsStripeConfiguration ? (
            <AlertTriangle size={16} className="text-amber-500" />
          ) : (
            <DollarSign size={16} />
          ),
          locked: !hasOnlinePayment
        },
      ]
    },
    {
      category: t('sidebar.parametres'),
      items: [
        { path: `/${currentLang}/provider/profile`, label: t('sidebar.profile'), icon: <UserCog size={16} /> },
      ]
    }
  ];

  return (
      <>
        {/* Mobile sidebar backdrop */}
        {isSidebarOpen && (
            <div
                className="fixed inset-0 z-20 bg-black bg-opacity-50 lg:hidden"
                onClick={closeSidebar}
            ></div>
        )}

        <aside
            className={`fixed inset-y-0 left-0 z-30 flex flex-col bg-white dark:bg-gray-800 shadow-lg transition-all duration-300 ease-in-out transform ${
                isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
            } lg:translate-x-0 ${
                isCollapsed ? 'w-16' : 'w-52'
            } rounded-r-2xl border-r border-gray-100 dark:border-gray-700 hidden lg:flex`}
        >
          {/* Header */}
          <div className="h-14 px-3 flex items-center justify-between border-b border-gray-200 dark:border-gray-700 rounded-tr-2xl">
            {!isCollapsed && (
                <img 
                  src={theme === 'dark' ? '/prestaslot-logo-dark.svg' : '/prestaslot_logo.svg'} 
                  alt="PrestaSlot Logo" 
                  className="h-8"
                />
            )}
            <button
                className="p-1.5 rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none transition-all duration-200 hover:shadow-md"
                onClick={toggleCollapsed}
                aria-label="Toggle sidebar"
            >
              <ChevronLeft size={16} className={`transform transition-transform ${isCollapsed ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {/* Company Selector */}
          <div className="px-3 py-3 border-b border-gray-200 dark:border-gray-700">
            {activeCompany ? (
              <div className="relative">
                <button
                  onClick={() => setIsCompanyDropdownOpen(!isCompanyDropdownOpen)}
                  className={`flex items-center gap-2 ${isCollapsed ? 'justify-center' : 'w-full'}`}
                >
                  {activeCompany.companyLogo ? (
                    <img
                      src={activeCompany.companyLogo}
                      alt={activeCompany.businessName}
                      className="w-7 h-7 rounded-full object-cover ring-2 ring-primary/20"
                    />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center ring-2 ring-primary/20">
                      <Camera size={14} className="text-gray-500 dark:text-gray-400" />
                    </div>
                  )}
                  {!isCollapsed && (
                    <div className="flex items-center justify-between w-full">
                      <span className="font-medium text-sm">{activeCompany.businessName}</span>
                      <ChevronDown size={14} className={`text-gray-400 ${isCompanyDropdownOpen ? 'rotate-180' : ''}`} />
                    </div>
                  )}
                </button>
                
                {isCompanyDropdownOpen && companyStore.getCompanies && companyStore.getCompanies.length > 0 && (
                  <div className="absolute z-50 mt-2 w-full bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 left-0">
                    {companyStore.getCompanies.map((company: Company) => (
                      <button
                        key={company.id}
                        onClick={() => handleCompanyChange(company)}
                        className="w-full flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        {company.companyLogo ? (
                          <img src={company.companyLogo} alt={company.businessName} className="w-5 h-5 rounded-full object-cover" />
                        ) : (
                          <div className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                            <Camera size={10} className="text-gray-500 dark:text-gray-400" />
                          </div>
                        )}
                        <span>{company.businessName}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              // Afficher un état de chargement lorsque activeCompany est undefined
              <div className="flex items-center justify-center h-8">
                <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin"></div>
              </div>
            )}
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-3 px-2">
            {navGroups.map((group, index) => (
                <div key={index} className="mb-6">
                  {!isCollapsed && (
                      <h3 className="px-3 mb-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {group.category}
                      </h3>
                  )}
                  <ul className="space-y-1">
                    {group.items.map((item: any) => (
                        <li key={item.path}>
                          {item.locked ? (
                            <div
                              className={`flex items-center justify-between px-3 py-2 rounded-xl text-sm cursor-pointer opacity-50 hover:opacity-70 transition-opacity ${
                                isCollapsed ? 'justify-center' : 'ml-2'
                              } text-gray-400 dark:text-gray-500`}
                              title="Fonctionnalité Premium"
                              onClick={() => {
                                setLockedFeature({ name: item.label, plan: 'Premium' });
                                setIsUpgradeModalOpen(true);
                              }}
                            >
                              <div className="flex items-center">
                                <span className={`flex-shrink-0 ${isCollapsed ? '' : 'mr-3'}`}>{item.icon}</span>
                                {!isCollapsed && <span>{item.label}</span>}
                              </div>
                              {!isCollapsed && (
                                <Lock size={14} className="ml-2 text-amber-500" />
                              )}
                            </div>
                          ) : (
                            <NavLink
                              to={item.path}
                              className={({ isActive }) =>
                                  `flex items-center px-3 py-2 rounded-xl transition-all duration-200 text-sm ${
                                      isActive
                                          ? 'bg-primary/5 dark:bg-primary-dark/20 text-primary dark:text-primary-light font-medium shadow-sm'
                                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:shadow-sm'
                                  } ${isCollapsed ? 'justify-center' : 'ml-2'}`
                              }
                              onClick={() => window.innerWidth < 1024 && closeSidebar()}
                            >
                              <span className={`flex-shrink-0 ${isCollapsed ? '' : 'mr-3'}`}>{item.icon}</span>
                              {!isCollapsed && <span>{item.label}</span>}
                            </NavLink>
                          )}
                        </li>
                    ))}
                  </ul>
                </div>
            ))}
          </nav>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 p-3 rounded-br-2xl mt-2">
            {/* Theme Toggle */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                {!isCollapsed && (
                    <span className="text-sm text-gray-600 dark:text-gray-400">Mode {t('sidebar.dark', 'sombre')}</span>
                )}
                <button
                    role="switch"
                    aria-checked={theme === 'dark'}
                    onClick={toggleTheme}
                    className="relative inline-block w-12 h-6 rounded-full focus:outline-none hover:opacity-90 transition-opacity"
                >
                  <div className={`absolute inset-0 rounded-full transition-colors duration-200 ease-in-out ${
                      theme === 'dark' ? 'bg-primary' : 'bg-gray-200'
                  }`}></div>
                  <div
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transform transition-transform duration-200 ease-in-out ${
                          theme === 'dark' ? 'translate-x-6' : 'translate-x-0'
                      }`}
                  ></div>
                </button>
              </div>
            </div>

            {/* User Profile */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-full bg-primary/10 dark:bg-primary-dark/30 flex items-center justify-center text-primary dark:text-primary-light text-sm font-medium flex-shrink-0 border-2 border-primary/20 dark:border-primary-dark/40 shadow-sm overflow-hidden">
                {professionalStore.getProfileProfessional?.picture ? (
                  <img
                    src={professionalStore.getProfileProfessional.picture}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  professionalStore.getInitialProfil
                )}
              </div>
              {!isCollapsed && (
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{professionalStore.getProfileProfessional?.firstName} {professionalStore.getProfileProfessional?.lastName}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{professionalStore.getProfileProfessional?.profession || t('sidebar.profession', 'Profession ?')}</p>
                  </div>
              )}
            </div>

            {/* Logout Button */}
            <button
                className={`w-full flex items-center px-3 py-2 rounded-xl text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 hover:shadow-sm ${
                    isCollapsed ? 'justify-center' : ''
                }`}
                onClick={() => setIsLogoutDialogOpen(true)}
            >
              <LogOut size={16} className={isCollapsed ? '' : 'mr-3'} />
              {!isCollapsed && <span>{t('sidebar.logout', 'Déconnexion')}</span>}
            </button>
            

          </div>
        </aside>
        {/* Logout Confirmation Modal */}
        <LogoutConfirmationModal
          isOpen={isLogoutDialogOpen}
          onClose={() => setIsLogoutDialogOpen(false)}
          onConfirm={() => authViewModel.logout()}
        />

        {/* Upgrade Required Modal */}
        <UpgradeRequiredModal
          isOpen={isUpgradeModalOpen}
          onClose={() => setIsUpgradeModalOpen(false)}
          featureName={lockedFeature.name}
          requiredPlan={lockedFeature.plan}
        />

      </>
  );
});

export default Sidebar;